import { computed, inject, type Signal } from '@angular/core';
import { _ } from '@ngx-translate/core';
import { ThemeService, type ThemeModePreference } from '@sb/shared/theme';

export interface ThemeChoiceOption {
  value: ThemeModePreference;
  /** English source string — pipe it through `translate` where it is rendered. */
  label: string;
}

/** `system` last: it is the deferral, offered after the two concrete modes it defers between. */
export const THEME_CHOICES: readonly ThemeChoiceOption[] = [
  { value: 'light', label: _('Light') },
  { value: 'dark', label: _('Dark') },
  { value: 'system', label: _('Match system') },
];

export interface ThemeChoicePicker {
  options: readonly ThemeChoiceOption[];
  /** The stored preference, so `system` shows as its own option rather than as the mode it resolves to. */
  current: Signal<ThemeModePreference>;
  /** The active choice's English label, for a trigger that shows what is selected without opening. */
  label: Signal<string>;
  /** Ignores anything that is not a {@link ThemeModePreference}, so a widened value can be passed straight in. */
  select(value: string | undefined): void;
}

/**
 * Everything a theme picker needs, resolved from the injection context: the options, the active
 * choice and its label, and the one way to change it.
 */
export function useThemeChoice(): ThemeChoicePicker {
  const theme = inject(ThemeService);
  const current = theme.modePreference;

  return {
    options: THEME_CHOICES,
    current,
    label: computed(() => THEME_CHOICES.find((option) => option.value === current())?.label ?? ''),
    select(value: string | undefined): void {
      const choice = THEME_CHOICES.find((option) => option.value === value);

      if (!choice) {
        return;
      }

      theme.setModePreference(choice.value);
    },
  };
}
